'use client'

// Глобальный fallback для 404. Показывается когда Next.js не может найти страницу.
// Это лучше дефолтного «404 This page could not be found» — даёт пользователю контекст и ссылки.
// v2: показывает локаль из URL, ссылка на список тендеров и сделок.

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { AlertTriangle, ArrowLeft, Home, FileText, Briefcase } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslations } from 'next-intl'
import * as Sentry from '@sentry/nextjs'

// Тело страницы «не найдено». Язык — из провайдера, который ставит
// app/not-found.tsx (единый выбор: адрес → cookie → русский), 905.
export function NotFoundClient() {
  const tn = useTranslations('shared.notFound')
  const pathname = usePathname()

  useEffect(() => {
    // 404 не считается ошибкой и НЕ отправляется в Sentry автоматически.
    // Репортим ТОЛЬКО 404 с внутреннего перехода (same-origin referrer) — это
    // сигнал реально битой ссылки. Прямые заходы и внешние рефереры — это боты
    // и сканеры (open-redirect пробы /goto?link=, .php-сканы), которые заваливали
    // GlitchTip сотнями шумовых issue. Все 404 группируются в один issue.
    const ref = typeof document !== 'undefined' ? document.referrer : ''
    const sameOrigin =
      !!ref && typeof window !== 'undefined' && ref.startsWith(window.location.origin)
    if (pathname && sameOrigin) {
      Sentry.captureMessage(`404 Not Found: ${pathname}`, {
        level: 'warning',
        fingerprint: ['route-404'],
        tags: { kind: '404', path: pathname },
        extra: { path: pathname, referrer: ref || null },
      })
    }
  }, [pathname])

  // Угадаем какой раздел пытался открыть пользователь
  const section = pathname?.match(/\/(tenders|deals|partners|fleet|settings)/)?.[1]

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-ink-50/40">
      <div className="max-w-lg w-full bg-white rounded-xl border border-ink-300/60 shadow-sm p-8 text-center">
        <div className="mx-auto h-16 w-16 rounded-full bg-amber-50 flex items-center justify-center">
          <AlertTriangle className="h-8 w-8 text-amber-500" />
        </div>
        <h1 className="mt-4 text-2xl font-bold text-ink-900">{tn('title')}</h1>
        <p className="mt-2 text-sm text-ink-500">
          {tn('hint')}
        </p>
        {pathname && (
          <p className="mt-2 text-xs text-ink-500 font-mono break-all">
            {pathname}
          </p>
        )}

        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button
            onClick={() => history.back()}
            className="inline-flex items-center gap-2 rounded-lg border border-ink-300 bg-white px-4 py-2.5 text-sm font-medium text-ink-700 hover:bg-ink-100"
          >
            <ArrowLeft size={16} /> {tn('back')}
          </button>
          <Link
            href={`/dashboard`}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700"
          >
            <Home size={16} /> {tn('home')}
          </Link>
        </div>

        {/* Быстрые ссылки на разделы — особенно полезно когда тендер/сделка не нашлись */}
        {section && (
          <div className="mt-4 pt-4 border-t border-ink-300/40">
            <p className="text-xs text-ink-500 mb-2">{tn('openList')}</p>
            <div className="flex flex-wrap justify-center gap-2">
              <Link
                href={`/tenders`}
                className="inline-flex items-center gap-1 rounded-lg border border-ink-300 bg-white px-3 py-1.5 text-xs text-ink-700 hover:bg-ink-50"
              >
                <FileText size={12} /> {tn('tenders')}
              </Link>
              <Link
                href={`/deals`}
                className="inline-flex items-center gap-1 rounded-lg border border-ink-300 bg-white px-3 py-1.5 text-xs text-ink-700 hover:bg-ink-50"
              >
                <Briefcase size={12} /> {tn('deals')}
              </Link>
            </div>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-ink-300/40 text-xs text-ink-500 space-y-1 text-left">
          <p className="font-medium text-ink-700 mb-2">{tn('reasons')}</p>
          <p>• {tn('reasonGone')}</p>
          <p>• {tn('reasonAccess')}</p>
          <p>• {tn('reasonDeploy')}</p>
        </div>
      </div>
    </div>
  )
}
